'use client';
import { useSkin } from 'contexts/SkinProvider';
import React from 'react';

/**
 * Mask geometry. Arbitrary properties take no `tw-` prefix, and Tailwind's
 * autoprefixer emits the `-webkit-mask-*` twins.
 *
 * `inline-block` matches the <img> this replaces: a block-level box with
 * `width: auto` is stretch-fit, and CSS then ignores aspect-ratio — so the mark
 * would span its container instead of sizing off its height.
 */
const MASK_CLASSES =
  'tw-inline-block tw-align-middle [mask-repeat:no-repeat] [mask-position:center] [mask-size:contain]';

interface ThemedSvgProps {
  /**
   * A **flat single-colour** SVG. Multi-colour art (e.g. brand/minthr-logo.svg,
   * whose wordmark is black next to a green mark) collapses to one colour when
   * masked — render those as a plain <img>.
   */
  src: string;
  /**
   * viewBox ratio of `src`, e.g. `'137 / 137'`. Required: a masked <span> has no
   * intrinsic size, so it silently collapses without one. Call sites that fix
   * both width and height override it, as CSS then ignores aspect-ratio.
   */
  aspect: string;
  /** Empty for decorative art that already sits beside a text label. */
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Paints a single-colour SVG in the active theme's accent hex
 * (`useSkin().primaryColor`) by using the art as a CSS mask over a coloured
 * box. Switching theme re-colours it on the next render — one asset, no
 * per-skin copy, and nothing reads a `/skin/*.css` variable back out.
 */
function ThemedSvg({
  src,
  aspect,
  alt = '',
  className,
  style,
}: ThemedSvgProps): JSX.Element {
  const { primaryColor } = useSkin();

  return (
    <span
      role="img"
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      className={`${MASK_CLASSES} ${className ?? ''}`}
      style={{
        aspectRatio: aspect,
        ...style,
        backgroundColor: primaryColor,
        // Inline, not an arbitrary class, so `src` stays a prop. React inline
        // styles get no autoprefixing, hence both spellings.
        WebkitMaskImage: `url('${src}')`,
        maskImage: `url('${src}')`,
      }}
    />
  );
}

export default ThemedSvg;
